import React, { useMemo, useCallback, useEffect, useRef } from 'react'; // Added useRef
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { useBacktestApi } from '../../hooks/useBacktestApi';
import { useAuth } from '../../context/AuthContext';
import useHistoryManagement from '../../hooks/useHistoryManagement';
import useFormState from '../../hooks/useFormState';
import useToast from '../../hooks/useToast';
import styles from './TraderDashboard.module.css';

// Import extracted components
import BacktestForm from './BacktestForm';
import EmailVerificationView from './EmailVerificationView';
import LoadingView from './LoadingView';
import ResultsSection from './ResultsSection';
import HistoryTable from './HistoryTable';
import Toast from './Toast';

function TraderDashboard() {
  const { user, loading: authLoading, isAuthenticated, checkEmailVerified, forceTokenRefresh, sendVerificationEmail } = useAuth();
  
  // 1. PIN THE SYNC GUARD
  const hasRefreshedRef = useRef(false);
  
  const [checking, setChecking] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [resendStatus, setResendStatus] = React.useState('');

  const {
    params,
    localError,
    handleInputChange,
    handleIndexChange,
  } = useFormState();

  const {
    toastMessage,
    showToast,
    clearToast,
  } = useToast();

  const {
    loading: apiLoading,
    error: apiError,
    results,
    jobStatus,
    progress,
    runBacktest,
    cancelJob,
    downloadAndReset,
    getBacktestHistory,
    isRunning,
    isCompleted,
    canDownload,
    authChecking,
  } = useBacktestApi();

  const {
    backtestHistory,
    historyLoading,
    refreshHistory,
  } = useHistoryManagement({
    getBacktestHistory,
    isAuthenticated,
    user,
    authLoading,
  });

  const isLoading = useMemo(() => apiLoading || authChecking, [apiLoading, authChecking]);

  // 2. STABILIZED COMPLETION EFFECT
  useEffect(() => {
    // If job just finished and we haven't synced yet
    if (jobStatus === 'completed' && !hasRefreshedRef.current) {
      console.log('[Dashboard] ✅ Job complete trigger: refreshing history');
      hasRefreshedRef.current = true;
      refreshHistory();
    }
    
    // Reset the guard when a new job starts or form is reset
    if (jobStatus === 'idle' || jobStatus === 'queued') {
      hasRefreshedRef.current = false;
    }
  }, [jobStatus, refreshHistory]);

  const handleVerifyEmail = useCallback(async () => {
    setChecking(true);
    const isVerified = await checkEmailVerified(); 
    if (isVerified) {
      await forceTokenRefresh();
    } else {
      setResendStatus("Still not verified. Try clicking the link in your email again.");
    }
    setChecking(false);
  }, [checkEmailVerified, forceTokenRefresh]);

  const handleResendVerification = useCallback(async () => {
    setResending(true);
    setResendStatus('Sending...');
    const result = await sendVerificationEmail(user.email);
    if (result.success) {
      setResendStatus('New link sent! Check your inbox.');
    } else {
      setResendStatus(`Error: ${result.error}`);
    }
    setResending(false);
  }, [sendVerificationEmail, user?.email]);

  // Error Handling Toast
  useEffect(() => {
    const errorMsg = apiError || localError;
    if (errorMsg) {
      showToast(errorMsg);
    }
  }, [apiError, localError, showToast]);

  const handleRunBacktest = useCallback(async (e) => {
    e.preventDefault();
    await runBacktest(params);
  }, [runBacktest, params]);

  const handleCancelBacktest = useCallback(() => {
      cancelJob();
  }, [cancelJob]);

  const formatValue = useCallback((val, isPercent = false) => {
    if (val == null) return 'N/A';
    return isPercent ? `${(val * 100).toFixed(2)}%` : val.toFixed(2);
  }, []);

  if (authLoading) return <LoadingView />;

  if (isAuthenticated && !user?.emailVerified) {
    return (
      <EmailVerificationView
        user={user}
        onVerifyEmail={handleVerifyEmail}
        onResendVerification={handleResendVerification}
        checking={checking}
        resending={resending}
        resendStatus={resendStatus}
      />
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.traderToolSection}>
        <div className={styles.backTestFormCard}>
          <BacktestForm
            params={params}
            onInputChange={handleInputChange}
            onIndexChange={handleIndexChange}
            isJobRunning={isRunning}
            isJobCompleted={isCompleted}
            onSubmit={handleRunBacktest}
            onCancel={handleCancelBacktest}
            onDownload={downloadAndReset}
            isLoading={isLoading}
            canDownload={canDownload}
            localError={localError}
          />
        </div>
        
        <div className={styles.resultsCard}>
          <h2 className={styles.cardTitle}>Live Status</h2>
          <ResultsSection
            isRunning={isRunning}
            isCompleted={isCompleted}
            jobStatus={jobStatus}
            progress={progress}
            results={results}
            formatValue={formatValue}
          />
        </div>
        
        <div className={styles.historyCard}>
          <div className={styles.cardTitle}>
            Backtest History
            <button 
            onClick={refreshHistory} 
            className={styles.refreshButton}
            disabled={historyLoading || !isAuthenticated}
            aria-label={historyLoading ? "Refreshing history..." : "Refresh history"}
          >
            <FontAwesomeIcon icon={faSyncAlt} spin={historyLoading} />
            <span className="sr-only">
              {historyLoading ? "Refreshing history..." : "Refresh history"}
            </span>
          </button>
          </div>

          <div className={styles.historyTableContainer}>
            <HistoryTable
              backtestHistory={backtestHistory}
              historyLoading={historyLoading}
              formatValue={formatValue}
            />
          </div>
        </div>
      </div>

      <Toast message={toastMessage} onDismiss={clearToast} />
    </div>
  );
}

export default TraderDashboard;